import * as React from 'react';
import type {ComponentPropsWithoutRef, HTMLAttributes} from 'react';
import {MessageScroller} from '@shadcn/react/message-scroller';
import {cn} from '../lib/utils';

export type ConversationProps = ComponentPropsWithoutRef<'div'>;

/**
 * Scrollable transcript viewport, backed by the headless `@shadcn/react`
 * MessageScroller. `preserveScrollOnPrepend` keeps the visible row stable when
 * older history is loaded above; live-edge following and user scroll-up opt-out
 * are owned by the MessageScroller.Provider in ChatConversation.
 *
 * Do not put vertical padding on this viewport (e.g. `pb-20` via className).
 * MessageScroller's at-bottom detection only accounts for padding on
 * ConversationContent, so viewport padding inflates the auto-follow re-engage
 * zone and yanks users back to the bottom when they scroll up during
 * streaming. Put transcript padding on `contentClassName` instead.
 */
export const Conversation = React.forwardRef<HTMLDivElement, ConversationProps>(
  ({className, children, ...props}, ref) => (
    <MessageScroller.Viewport
      ref={ref}
      preserveScrollOnPrepend
      className={cn(
        'scrollbar-overlay scroll-fade flex min-h-0 w-full flex-1 flex-col overflow-y-auto',
        className,
      )}
      {...props}
    >
      {children}
    </MessageScroller.Viewport>
  ),
);
Conversation.displayName = 'Conversation';

export type ConversationContentProps = ComponentPropsWithoutRef<'div'>;

export const ConversationContent = React.forwardRef<
  HTMLDivElement,
  ConversationContentProps
>(({className, ...props}, ref) => (
  <MessageScroller.Content
    ref={ref}
    className={cn('grid w-full gap-4 py-4', className)}
    {...props}
  />
));
ConversationContent.displayName = 'ConversationContent';

export type ConversationEmptyStateProps = HTMLAttributes<HTMLDivElement>;

export const ConversationEmptyState = React.forwardRef<
  HTMLDivElement,
  ConversationEmptyStateProps
>(({className, ...props}, ref) => (
  <div
    ref={ref}
    className={cn(
      'flex size-full flex-col items-center justify-center gap-2 text-center',
      className,
    )}
    {...props}
  />
));
ConversationEmptyState.displayName = 'ConversationEmptyState';
